import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Building2, Coins, Clock, TrendingUp, Loader2, Users } from "lucide-react";
import OnboardingProgress from "@/components/OnboardingProgress";

const statusStyle = { CONFIRMED: "status-confirmed", PENDING_CASH: "status-pending", PENDING_PAYMENT: "status-pending", CANCELLED: "status-cancelled" };
const statusLabel = { CONFIRMED: "Confirmed", PENDING_CASH: "Pay at Venue", PENDING_PAYMENT: "Awaiting Pay", CANCELLED: "Cancelled" };

export default function ProviderDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/provider/dashboard").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return <div className="text-center py-20 font-heading text-xl">Unable to load dashboard</div>;

  const stats = [
    { label: "Total Bookings", value: data.total_bookings, icon: CalendarDays, color: "text-court-orange" },
    { label: "Upcoming", value: data.upcoming, icon: Clock, color: "text-blue-500" },
    { label: "Revenue", value: `₱${data.revenue?.toLocaleString()}`, icon: Coins, color: "text-lime-volt", click: () => navigate("/revenue-center?view=venue") },
    { label: "Venues", value: data.venues_count, icon: Building2, color: "text-purple-500" },
    { label: "Pending Cash", value: data.pending_cash, icon: Users, color: "text-amber-500" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="provider-dashboard">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-2">Dashboard</h1>
      <p className="text-muted-foreground mb-6">Overview of your venues and bookings</p>

      {/* Onboarding Progress Widget */}
      <div className="mb-8">
        <OnboardingProgress compact />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        {stats.map((s, i) => (
          <div key={i} onClick={s.click} className={`bg-white border-2 border-slate-100 p-4 hover:border-slate-200 transition-colors ${s.click ? 'cursor-pointer hover:-translate-y-0.5' : ''}`} data-testid={`stat-${i}`}>
            <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
            <p className="font-heading text-2xl font-bold">{s.value}</p>
            <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Bookings */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-court-orange" />
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Recent Bookings</h2>
        </div>
        {data.recent_bookings?.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No bookings yet</p>
        ) : (
          <div className="space-y-2">
            {data.recent_bookings?.map(b => (
              <div key={b.booking_id} className="bg-white border border-slate-100 p-3 flex items-center gap-4 text-sm" data-testid={`recent-booking-${b.booking_id}`}>
                <div className="flex-1">
                  <p className="font-medium">{b.user_name || b.user_email}</p>
                  <p className="text-muted-foreground text-xs">{b.resource_name} at {b.venue_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{b.start_dt?.slice(0, 10)} {b.start_dt?.slice(11, 16)}</p>
                  <Badge className={`${statusStyle[b.status]} text-[10px] font-heading uppercase`}>{statusLabel[b.status] || b.status}</Badge>
                </div>
                <p className="font-heading font-bold text-court-orange">₱{b.total_amount?.toFixed(0)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
